import axios, { type AxiosResponse } from "axios";

import {
  normalizeClinicalProductionList,
  type ClinicalProductionListPayload,
} from "@/lib/clinicalProductionPagination";
import {
  normalizeTotalPaginatedPayload,
  type TotalPaginatedPayload,
} from "@/lib/totalPagination";

import type {
  AuthCredentials,
  CatalogItemListParams,
  ClinicalFormTemplateListParams,
  ClinicOnboardingResponse,
  CreateCatalogItemPayload,
  CreateClinicalFormTemplatePayload,
  CreateAppointmentPayload,
  CreateAccountReceivablePayload,
  CreateCampaignPayload,
  CreateClinicalEvolutionPayload,
  FinalizeClinicalRecordPayload,
  CorrectClinicalRecordPayload,
  ArchiveClinicalRecordPayload,
  CreateClinicPayload,
  CreateClinicMetricPeriodPayload,
  CreateClinicUserPayload,
  CreateSuperAdminPayload,
  ImportZenFisioAppointmentsPayload,
  ImportZenFisioAppointmentsResult,
  ImportZenFisioClinicalPayload,
  ImportZenFisioClinicalResult,
  ImportZenFisioFinancialPayload,
  ImportZenFisioFinancialResult,
  ImportZenFisioPatientsPayload,
  ImportZenFisioPatientsResult,
  ImportZenFisioProfessionalsPayload,
  ImportZenFisioProfessionalsResult,
  FinancialListParams,
  FinancialPeriodSummary,
  FinancialIndicatorDetailResponse,
  FinancialIndicatorDetailType,
  DashboardIndicatorDetailResponse,
  DashboardIndicatorDetailType,
  ClinicalProductionIndicatorDetailResponse,
  ClinicalProductionIndicatorDetailType,
  SoldPlansReport,
  AttendanceDetailListParams,
  AttendanceDetailResponse,
  LeadListParams,
  LeadListSummary,
  LeadNoBookingOriginDetailParams,
  LeadNoBookingOriginParams,
  LeadNoBookingOriginSummary,
  LeadNoScheduleDetailParams,
  MarketingSectorDetailItem,
  MarketingSectorDetailMetric,
  PaginatedResponse,
  PatientListParams,
  PatientIdentityCandidate,
  PatientIdentityCandidatePayload,
  ProfessionalListParams,
  RevertZenFisioImportResult,
  CreateExpensePayload,
  CreateLeadPayload,
  CreatePatientPayload,
  CreatePatientDocumentPayload,
  CreatePatientPhotoPayload,
  CreatePaymentMethodSettingPayload,
  CreateProfessionalPayload,
  CreateRevenuePayload,
  ReceiveAccountReceivablePayload,
  RefundRevenuePayload,
  RenewTreatmentPlanPayload,
  CancelTreatmentPlanPayload,
  ReactivateTreatmentPlanPayload,
  TreatmentPlanCancellationPreview,
  TreatmentPlanLifecycleResponse,
  CreateTreatmentPlanPayload,
  CreateTreatmentFeedbackPayload,
  PublicTreatmentFeedbackResponsePayload,
  RegisterPayload,
  ResetSuperAdminAccessPayload,
  UpdateClinicStatusPayload,
  UpdateClinicSettingsPayload,
  UpdateClinicMetricPeriodPayload,
  UpdateClinicUserPayload,
  UpdateCampaignPayload,
  UpdateCatalogItemPayload,
  UpdateClinicalFormTemplatePayload,
  UpdateAppointmentPayload,
  AppointmentCompletionCommandResponse,
  AppointmentCompletionReversalPreview,
  ReverseAppointmentCompletionPayload,
  UpdateAppointmentStatusSettingPayload,
  UpdateAccountReceivablePayload,
  UpdateClinicalEvolutionPayload,
  UpdateLeadPayload,
  UpdatePatientPayload,
  UpdatePaymentMethodSettingPayload,
  UpdateProfessionalPayload,
  UpdateTreatmentPlanPayload,
  UpdateSuperAdminStatusPayload,
  ZenFisioImportHistoryResponse,
} from "@/types/api";
import type {
  Appointment,
  AppointmentStatusSetting,
  AccountReceivable,
  AppUser,
  Campaign,
  CatalogItem,
  CareExperienceMetrics,
  ClinicalFormTemplate,
  ClinicalFormTemplateSuggestion,
  ClinicalEvolution,
  ClinicalHistoryPage,
  ClinicalRecordRevision,
  ClinicalRecordCorrection,
  ClinicalProduction,
  ClinicalProductionSummary,
  ClinicUser,
  Clinic,
  ClinicMetricPeriod,
  DashboardMetrics,
  EntityId,
  Expense,
  ExpenseAllocationApplication,
  ExpenseAllocationApplicationList,
  ExpenseAllocationApplicationStatus,
  ExpenseAllocationEligibility,
  ExpenseAllocationSimulation,
  ExpenseAllocationRule,
  ExpenseAllocationSettings,
  ExpenseCategoryRecord,
  FinancialCategoryRecord,
  ExpensePayable,
  ExpenseSummary,
  Lead,
  LeadNoScheduleReasonCatalogItem,
  LeadStatus,
  MarketingMetrics,
  ManagementExpenseAllocationImpact,
  ManagementOverview,
  ManagementOverviewClassificationMode,
  ManagerialIncomeStatement,
  ManagerialIncomeStatementAllocationView,
  ManagerialIncomeStatementClassificationMode,
  ManagerialIncomeStatementLineCode,
  ManagerialCompetenceClassification,
  OperationalAlertsSummary,
  Patient,
  PatientDocument,
  PaymentMethodSetting,
  ProfessionalPayout,
  ProfessionalPayoutRule,
  ProfessionalPayoutSummary,
  ProfessionalPayoutRuleType,
  Professional,
  PublicNpsSurvey,
  Revenue,
  Sector,
  StorageQuota,
  SuperAdminAuditLog,
  SuperAdminClinic,
  SuperAdminUser,
  TreatmentPlan,
  TreatmentPlanStatus,
  TreatmentFeedback,
  TreatmentFeedbackRequest,
} from "@/types/domain";

const rawApiUrl =
  window.__APP_CONFIG__?.backendUrl ??
  import.meta.env.REACT_APP_BACKEND_URL ??
  (window.location.hostname === "localhost" ? "http://localhost:8001" : "");
const API_URL = rawApiUrl.replace(/\/+$/, "");
type ApiResult<TData> = Promise<AxiosResponse<TData>>;
type ExpenseListParams = Record<string, string | number | undefined>;
type ManagerialReportFormat = "xlsx" | "pdf";

function isPublicRoute(pathname: string): boolean {
  return pathname === "/" || pathname === "/login" || pathname === "/register" || pathname.startsWith("/nps/");
}

const api = axios.create({
  baseURL: API_URL ? `${API_URL}/api` : "/api",
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !isPublicRoute(window.location.pathname)) {
      window.location.href = "/login";
    }

    return Promise.reject(error);
  },
);

export const authApi = {
  onboardClinic: (data: RegisterPayload): ApiResult<ClinicOnboardingResponse> => api.post("/auth/onboarding/clinic", data),
  login: (data: AuthCredentials): ApiResult<AppUser> => api.post("/auth/login", data),
  register: (data: CreateClinicUserPayload): ApiResult<AppUser> => api.post("/auth/register", data),
  logout: (): ApiResult<void> => api.post("/auth/logout"),
  me: (): ApiResult<AppUser> => api.get("/auth/me"),
  refresh: (): ApiResult<AppUser> => api.post("/auth/refresh"),
};

export const clinicsApi = {
  list: (): ApiResult<Clinic[]> => api.get("/v1/clinics"),
  get: (id: EntityId): ApiResult<Clinic> => api.get(`/v1/clinics/${id}`),
  create: (data: CreateClinicPayload): ApiResult<Clinic> => api.post("/v1/clinics", data),
  updateSettings: (id: EntityId, data: UpdateClinicSettingsPayload): ApiResult<Clinic> =>
    api.patch(`/v1/clinics/${id}/settings`, data),
};

export const clinicMetricPeriodsApi = {
  list: (clinicId: EntityId): ApiResult<ClinicMetricPeriod[]> => api.get(`/v1/clinics/${clinicId}/metric-periods`),
  create: (clinicId: EntityId, data: CreateClinicMetricPeriodPayload): ApiResult<ClinicMetricPeriod> =>
    api.post(`/v1/clinics/${clinicId}/metric-periods`, data),
  update: (clinicId: EntityId, periodId: EntityId, data: UpdateClinicMetricPeriodPayload): ApiResult<ClinicMetricPeriod> =>
    api.patch(`/v1/clinics/${clinicId}/metric-periods/${periodId}`, data),
  delete: (clinicId: EntityId, periodId: EntityId): ApiResult<void> =>
    api.delete(`/v1/clinics/${clinicId}/metric-periods/${periodId}`),
};

export const catalogItemsApi = {
  list: (clinicId: EntityId, params: CatalogItemListParams = {}): ApiResult<PaginatedResponse<CatalogItem>> =>
    api.get("/v1/catalog-items", { params: { clinic_id: clinicId, ...params } }),
  selectOptions: (
    clinicId: EntityId,
    params: Omit<CatalogItemListParams, "status" | "page" | "page_size"> = {},
  ): ApiResult<CatalogItem[]> =>
    api.get("/v1/catalog-items/select-options", { params: { clinic_id: clinicId, ...params } }),
  create: (data: CreateCatalogItemPayload): ApiResult<CatalogItem> => api.post("/v1/catalog-items", data),
  update: (id: EntityId, data: UpdateCatalogItemPayload): ApiResult<CatalogItem> =>
    api.patch(`/v1/catalog-items/${id}`, data),
  activate: (id: EntityId): ApiResult<CatalogItem> => api.post(`/v1/catalog-items/${id}/activate`),
  deactivate: (id: EntityId): ApiResult<CatalogItem> => api.post(`/v1/catalog-items/${id}/deactivate`),
};

export const clinicalFormTemplatesApi = {
  list: (clinicId: EntityId, params: ClinicalFormTemplateListParams = {}): ApiResult<ClinicalFormTemplate[]> =>
    api.get("/v1/clinical-form-templates", { params: { clinic_id: clinicId, ...params } }),
  get: (clinicId: EntityId, id: EntityId): ApiResult<ClinicalFormTemplate> =>
    api.get(`/v1/clinical-form-templates/${id}`, { params: { clinic_id: clinicId } }),
  suggestions: (clinicId: EntityId, params: ClinicalFormTemplateListParams): ApiResult<ClinicalFormTemplateSuggestion> =>
    api.get("/v1/clinical-form-templates/suggestions", { params: { clinic_id: clinicId, ...params } }),
  create: (data: CreateClinicalFormTemplatePayload): ApiResult<ClinicalFormTemplate> =>
    api.post("/v1/clinical-form-templates", data),
  update: (id: EntityId, data: UpdateClinicalFormTemplatePayload): ApiResult<ClinicalFormTemplate> =>
    api.patch(`/v1/clinical-form-templates/${id}`, data),
  publish: (id: EntityId): ApiResult<ClinicalFormTemplate> =>
    api.post(`/v1/clinical-form-templates/${id}/publish`),
  createNewVersion: (id: EntityId): ApiResult<ClinicalFormTemplate> =>
    api.post(`/v1/clinical-form-templates/${id}/new-version`),
  duplicate: (id: EntityId): ApiResult<ClinicalFormTemplate> =>
    api.post(`/v1/clinical-form-templates/${id}/duplicate`),
  activate: (id: EntityId): ApiResult<ClinicalFormTemplate> =>
    api.post(`/v1/clinical-form-templates/${id}/activate`),
  deactivate: (id: EntityId): ApiResult<ClinicalFormTemplate> =>
    api.post(`/v1/clinical-form-templates/${id}/deactivate`),
};

export const usersApi = {
  list: (): ApiResult<ClinicUser[]> => api.get("/v1/users"),
  create: (data: CreateClinicUserPayload): ApiResult<ClinicUser> => api.post("/v1/users", data),
  update: (id: EntityId, data: UpdateClinicUserPayload): ApiResult<ClinicUser> => api.patch(`/v1/users/${id}`, data),
};

export const superAdminApi = {
  listClinics: (): ApiResult<SuperAdminClinic[]> => api.get("/super-admin/clinics"),
  updateClinicStatus: (id: EntityId, data: UpdateClinicStatusPayload): ApiResult<SuperAdminClinic> =>
    api.patch(`/super-admin/clinics/${id}/status`, data),
  listUsers: (): ApiResult<SuperAdminUser[]> => api.get("/super-admin/super-admins"),
  inviteUser: (data: CreateSuperAdminPayload): ApiResult<SuperAdminUser> => api.post("/super-admin/super-admins", data),
  updateUserStatus: (id: EntityId, data: UpdateSuperAdminStatusPayload): ApiResult<SuperAdminUser> =>
    api.patch(`/super-admin/super-admins/${id}/status`, data),
  resetUserAccess: (id: EntityId, data: ResetSuperAdminAccessPayload): ApiResult<SuperAdminUser> =>
    api.patch(`/super-admin/super-admins/${id}/reset-access`, data),
  listAuditLogs: (): ApiResult<SuperAdminAuditLog[]> => api.get("/super-admin/audit-logs"),
  listZenFisioImportHistory: (clinicId: EntityId): ApiResult<ZenFisioImportHistoryResponse> =>
    api.get("/super-admin/imports/zen-fisio/history", { params: { clinic_id: clinicId } }),
  revertZenFisioImportBatch: (batchId: EntityId): ApiResult<RevertZenFisioImportResult> =>
    api.post(`/super-admin/imports/zen-fisio/batches/${batchId}/revert`),
  importZenFisioPatients: (data: ImportZenFisioPatientsPayload): ApiResult<ImportZenFisioPatientsResult> =>
    api.post("/super-admin/imports/zen-fisio/patients", data),
  importZenFisioProfessionals: (data: ImportZenFisioProfessionalsPayload): ApiResult<ImportZenFisioProfessionalsResult> =>
    api.post("/super-admin/imports/zen-fisio/professionals", data),
  importZenFisioAppointments: (data: ImportZenFisioAppointmentsPayload): ApiResult<ImportZenFisioAppointmentsResult> =>
    api.post("/super-admin/imports/zen-fisio/appointments", data),
  importZenFisioFinancial: (data: ImportZenFisioFinancialPayload): ApiResult<ImportZenFisioFinancialResult> =>
    api.post("/super-admin/imports/zen-fisio/financial", data),
  importZenFisioClinicalAssessments: (data: ImportZenFisioClinicalPayload): ApiResult<ImportZenFisioClinicalResult> =>
    api.post("/super-admin/imports/zen-fisio/clinical-assessments", data),
  importZenFisioClinicalEvolutions: (data: ImportZenFisioClinicalPayload): ApiResult<ImportZenFisioClinicalResult> =>
    api.post("/super-admin/imports/zen-fisio/clinical-evolutions", data),
};

export const leadsApi = {
  list: (clinicId: EntityId, status?: LeadStatus): ApiResult<Lead[]> =>
    api.get("/v1/leads", { params: { clinic_id: clinicId, status } }),
  listPaginated: (clinicId: EntityId, params: LeadListParams): ApiResult<PaginatedResponse<Lead>> =>
    api.get("/v1/leads", { params: { clinic_id: clinicId, ...params } }),
  summary: (clinicId: EntityId, params: Omit<LeadListParams, "page" | "page_size">): ApiResult<LeadListSummary> =>
    api.get("/v1/leads/summary", { params: { clinic_id: clinicId, ...params } }),
  noScheduleDetails: (clinicId: EntityId, params: LeadNoScheduleDetailParams): ApiResult<PaginatedResponse<Lead>> =>
    api.get("/v1/leads/no-schedule-details", { params: { clinic_id: clinicId, ...params } }),
  noBookingByOrigin: (clinicId: EntityId, params: LeadNoBookingOriginParams): ApiResult<LeadNoBookingOriginSummary> =>
    api.get("/v1/leads/no-booking-origin-summary", { params: { clinic_id: clinicId, ...params } }),
  noBookingOriginDetails: (clinicId: EntityId, params: LeadNoBookingOriginDetailParams): ApiResult<PaginatedResponse<Lead>> =>
    api.get("/v1/leads/no-booking-origin-details", { params: { clinic_id: clinicId, ...params } }),
  create: (data: CreateLeadPayload): ApiResult<Lead> => api.post("/v1/leads", data),
  update: (id: EntityId, data: UpdateLeadPayload): ApiResult<Lead> => api.patch(`/v1/leads/${id}`, data),
  delete: (id: EntityId): ApiResult<void> => api.delete(`/v1/leads/${id}`),
};

export const patientsApi = {
  list: (clinicId: EntityId, search?: string): ApiResult<Patient[]> =>
    api.get("/v1/patients", { params: { clinic_id: clinicId, search } }),
  listPaginated: (clinicId: EntityId, params: PatientListParams): ApiResult<PaginatedResponse<Patient>> =>
    api.get("/v1/patients", { params: { clinic_id: clinicId, ...params } }),
  get: (id: EntityId): ApiResult<Patient> => api.get(`/v1/patients/${id}`),
  create: (data: CreatePatientPayload): ApiResult<Patient> => api.post("/v1/patients", data),
  findIdentityCandidates: (data: PatientIdentityCandidatePayload): ApiResult<{ data: PatientIdentityCandidate[] }> =>
    api.post("/v1/patients/identity/candidates", data),
  update: (id: EntityId, data: UpdatePatientPayload): ApiResult<Patient> => api.patch(`/v1/patients/${id}`, data),
  uploadPhoto: (id: EntityId, data: CreatePatientPhotoPayload): ApiResult<Patient> =>
    api.post(`/v1/patients/${id}/photo`, data),
  downloadPhoto: (id: EntityId): Promise<AxiosResponse<Blob>> =>
    api.get(`/v1/patients/${id}/photo`, { responseType: "blob" }),
  removePhoto: (id: EntityId): ApiResult<Patient> => api.delete(`/v1/patients/${id}/photo`),
};

export const leadNoScheduleReasonsApi = {
  list: (
    clinicId: EntityId,
    status: "active" | "inactive" | "all" = "active",
  ): ApiResult<LeadNoScheduleReasonCatalogItem[]> =>
    api.get("/v1/lead-no-schedule-reasons", {
      params: { clinic_id: clinicId, reason_status: status },
    }),
  create: (clinicId: EntityId, name: string): ApiResult<LeadNoScheduleReasonCatalogItem> =>
    api.post("/v1/lead-no-schedule-reasons", { clinic_id: clinicId, name }),
  update: (
    id: EntityId,
    data: { name?: string; is_active?: boolean },
  ): ApiResult<LeadNoScheduleReasonCatalogItem> =>
    api.patch(`/v1/lead-no-schedule-reasons/${id}`, data),
};

export const patientDocumentsApi = {
  list: (clinicId: EntityId, patientId: EntityId): ApiResult<PatientDocument[]> =>
    api.get("/v1/patient-documents", { params: { clinic_id: clinicId, patient_id: patientId } }),
  create: (data: CreatePatientDocumentPayload): ApiResult<PatientDocument> =>
    api.post("/v1/patient-documents", data),
  download: (id: EntityId, download = false): Promise<AxiosResponse<Blob>> =>
    api.get(`/v1/patient-documents/${id}/download`, {
      params: download ? { download: "1" } : undefined,
      responseType: "blob",
    }),
  getDownloadUrl: (id: EntityId, download = false): string => {
    const baseUrl = API_URL ? `${API_URL}/api` : "/api";
    return `${baseUrl}/v1/patient-documents/${id}/download${download ? "?download=1" : ""}`;
  },
  delete: (id: EntityId): ApiResult<void> =>
    api.delete(`/v1/patient-documents/${id}`),
};

export const storageApi = {
  getQuota: (): ApiResult<StorageQuota> => api.get("/storage/quota"),
};

export const clinicalEvolutionsApi = {
  list: (clinicId: EntityId, patientId: EntityId, search?: string): ApiResult<ClinicalEvolution[]> =>
    api.get("/v1/clinical-evolutions", { params: { clinic_id: clinicId, patient_id: patientId, search } }),
  create: (data: CreateClinicalEvolutionPayload): ApiResult<ClinicalEvolution> =>
    api.post("/v1/clinical-evolutions", data),
  update: (id: EntityId, data: UpdateClinicalEvolutionPayload): ApiResult<ClinicalEvolution> =>
    api.patch(`/v1/clinical-evolutions/${id}`, data),
  finalize: (id: EntityId, data: FinalizeClinicalRecordPayload): ApiResult<ClinicalEvolution> =>
    api.post(`/v1/clinical-evolutions/${id}/finalize`, data),
  correct: (id: EntityId, data: CorrectClinicalRecordPayload): ApiResult<ClinicalRecordCorrection> =>
    api.post(`/v1/clinical-evolutions/${id}/corrections`, data),
  history: (id: EntityId, page = 1, pageSize = 20): ApiResult<ClinicalHistoryPage> =>
    api.get(`/v1/clinical-evolutions/${id}/history`, { params: { page, page_size: pageSize } }),
  revision: (id: EntityId, revisionId: EntityId): ApiResult<ClinicalRecordRevision> =>
    api.get(`/v1/clinical-evolutions/${id}/revisions/${revisionId}`),
  correction: (id: EntityId, correctionId: EntityId): ApiResult<ClinicalRecordCorrection> =>
    api.get(`/v1/clinical-evolutions/${id}/corrections/${correctionId}`),
  archive: (id: EntityId, data: ArchiveClinicalRecordPayload): ApiResult<ClinicalEvolution> =>
    api.post(`/v1/clinical-evolutions/${id}/archive`, data),
};

export const professionalsApi = {
  list: (clinicId: EntityId): ApiResult<Professional[]> =>
    api.get("/v1/professionals", { params: { clinic_id: clinicId } }),
  listPaginated: (clinicId: EntityId, params: ProfessionalListParams): ApiResult<PaginatedResponse<Professional>> =>
    api.get("/v1/professionals", { params: { clinic_id: clinicId, ...params } }),
  create: (data: CreateProfessionalPayload): ApiResult<Professional> => api.post("/v1/professionals", data),
  update: (id: EntityId, data: UpdateProfessionalPayload): ApiResult<Professional> => api.patch(`/v1/professionals/${id}`, data),
};

export const appointmentsApi = {
  list: (
    clinicId: EntityId,
    startDate: string,
    endDate: string,
    professionalId?: EntityId,
    patientId?: EntityId,
  ): ApiResult<Appointment[]> =>
    api.get("/v1/appointments", {
      params: {
        clinic_id: clinicId,
        start_date: startDate,
        end_date: endDate,
        professional_id: professionalId,
        patient_id: patientId,
      },
    }),
  create: (data: CreateAppointmentPayload): ApiResult<Appointment> => api.post("/v1/appointments", data),
  update: (id: EntityId, data: UpdateAppointmentPayload): ApiResult<Appointment> =>
    api.patch(`/v1/appointments/${id}`, data),
  complete: (id: EntityId): ApiResult<AppointmentCompletionCommandResponse> =>
    api.post(`/v1/appointments/${id}/complete`),
  getCompletionReversalPreview: (id: EntityId): ApiResult<AppointmentCompletionReversalPreview> =>
    api.get(`/v1/appointments/${id}/completion-reversal-preview`),
  reverseCompletion: (id: EntityId, data: ReverseAppointmentCompletionPayload): ApiResult<AppointmentCompletionCommandResponse> =>
    api.post(`/v1/appointments/${id}/reverse-completion`, data),
};

export const appointmentStatusSettingsApi = {
  list: (clinicId: EntityId): ApiResult<AppointmentStatusSetting[]> =>
    api.get("/v1/appointment-status-settings", { params: { clinic_id: clinicId } }),
  update: (
    clinicId: EntityId,
    statuses: UpdateAppointmentStatusSettingPayload[],
  ): ApiResult<AppointmentStatusSetting[]> =>
    api.put("/v1/appointment-status-settings", { clinic_id: clinicId, statuses }),
};

export const treatmentPlansApi = {
  list: (
    clinicId: EntityId,
    patientId?: EntityId,
    professionalId?: EntityId,
    statuses?: TreatmentPlanStatus[],
  ): ApiResult<TreatmentPlan[]> =>
    api.get("/v1/treatment-plans", {
      params: {
        clinic_id: clinicId,
        patient_id: patientId,
        professional_id: professionalId,
        status: statuses?.join(","),
      },
    }),
  create: (data: CreateTreatmentPlanPayload): ApiResult<TreatmentPlan> => api.post("/v1/treatment-plans", data),
  update: (id: EntityId, data: UpdateTreatmentPlanPayload): ApiResult<TreatmentPlan> =>
    api.patch(`/v1/treatment-plans/${id}`, data),
  cancellationPreview: (id: EntityId): ApiResult<TreatmentPlanCancellationPreview> =>
    api.get(`/v1/treatment-plans/${id}/cancellation-preview`),
  cancel: (id: EntityId, data: CancelTreatmentPlanPayload): ApiResult<TreatmentPlanLifecycleResponse> =>
    api.post(`/v1/treatment-plans/${id}/cancel`, data),
  reactivate: (id: EntityId, data: ReactivateTreatmentPlanPayload): ApiResult<TreatmentPlanLifecycleResponse> =>
    api.post(`/v1/treatment-plans/${id}/reactivate`, data),
  renew: (id: EntityId, data: RenewTreatmentPlanPayload): ApiResult<TreatmentPlan> =>
    api.post(`/v1/treatment-plans/${id}/renew`, data),
};

export const sectorsApi = {
  list: (
    clinicId: EntityId,
    filters?: { search?: string; status?: "active" | "inactive" | "all" },
  ): ApiResult<Sector[]> =>
    api.get("/v1/sectors", {
      params: {
        clinic_id: clinicId,
        search: filters?.search,
        status: filters?.status,
      },
    }),
  create: (data: { clinic_id: EntityId; name: string }): ApiResult<Sector> =>
    api.post("/v1/sectors", data),
  update: (id: EntityId, data: { name?: string; is_active?: boolean }): ApiResult<Sector> =>
    api.patch(`/v1/sectors/${id}`, data),
};

export const campaignsApi = {
  list: (clinicId: EntityId): ApiResult<Campaign[]> =>
    api.get("/v1/campaigns", { params: { clinic_id: clinicId } }),
  create: (data: CreateCampaignPayload): ApiResult<Campaign> => api.post("/v1/campaigns", data),
  update: (id: EntityId, data: UpdateCampaignPayload): ApiResult<Campaign> => api.patch(`/v1/campaigns/${id}`, data),
  delete: (id: EntityId): ApiResult<void> => api.delete(`/v1/campaigns/${id}`),
};

export const financialApi = {
  listFinancialCategories: (
    clinicId: EntityId,
    type: "revenue" | "expense",
    status: "active" | "inactive" | "all" = "active",
  ): ApiResult<FinancialCategoryRecord[]> =>
    api.get("/v1/financial-categories", { params: { clinic_id: clinicId, type, status } }),
  createFinancialCategory: (data: { clinic_id: EntityId; type: "revenue" | "expense"; name: string }): ApiResult<FinancialCategoryRecord> =>
    api.post("/v1/financial-categories", data),
  updateFinancialCategory: (
    id: EntityId,
    data: { clinic_id: EntityId; type: "revenue" | "expense"; name?: string; is_active?: boolean },
  ): ApiResult<FinancialCategoryRecord> => api.patch(`/v1/financial-categories/${id}`, data),
  listPaymentMethodSettings: (clinicId: EntityId): ApiResult<PaymentMethodSetting[]> =>
    api.get("/v1/payment-method-settings", { params: { clinic_id: clinicId } }),
  createPaymentMethodSetting: (data: CreatePaymentMethodSettingPayload): ApiResult<PaymentMethodSetting> =>
    api.post("/v1/payment-method-settings", data),
  updatePaymentMethodSetting: (id: EntityId, data: UpdatePaymentMethodSettingPayload): ApiResult<PaymentMethodSetting> =>
    api.patch(`/v1/payment-method-settings/${id}`, data),
  deletePaymentMethodSetting: (id: EntityId): ApiResult<void> => api.delete(`/v1/payment-method-settings/${id}`),
  listRevenues: (clinicId: EntityId, startDate: string, endDate: string, patientId?: EntityId, sectorId?: string): ApiResult<Revenue[]> =>
    api.get("/v1/revenues", {
      params: {
        clinic_id: clinicId,
        start_date: startDate,
        end_date: endDate,
        patient_id: patientId,
        sector_id: sectorId,
      },
    }),
  listRevenuesPaginated: (clinicId: EntityId, params: FinancialListParams): ApiResult<PaginatedResponse<Revenue>> =>
    api.get("/v1/revenues", { params: { clinic_id: clinicId, ...params } }),
  getPeriodSummary: (clinicId: EntityId, startDate: string, endDate: string, sectorId?: string): ApiResult<FinancialPeriodSummary> =>
    api.get("/v1/financial/period-summary", {
      params: {
        clinic_id: clinicId,
        start_date: startDate,
        end_date: endDate,
        sector_id: sectorId,
      },
    }),
  indicatorDetails: (clinicId: EntityId, params: {
    detail_type: FinancialIndicatorDetailType; start_date: string; end_date: string; sector_id?: string;
    search?: string; sort_by?: "recent" | "name" | "value"; sort_order?: "asc" | "desc";
    page: number; page_size: 20 | 50 | 100;
  }): ApiResult<FinancialIndicatorDetailResponse> =>
    api.get("/v1/financial/indicator-details", { params: { clinic_id: clinicId, ...params } }),
  getSoldPlansReport: (clinicId: EntityId, startDate: string, endDate: string, sectorId?: string): ApiResult<SoldPlansReport> =>
    api.get("/v1/financial/sold-plans-report", {
      params: {
        clinic_id: clinicId,
        start_date: startDate,
        end_date: endDate,
        sector_id: sectorId,
      },
    }),
  createRevenue: (data: CreateRevenuePayload): ApiResult<Revenue> => api.post("/v1/revenues", data),
  deleteRevenue: (id: EntityId): ApiResult<void> => api.delete(`/v1/revenues/${id}`),
  refundRevenue: (id: EntityId, data: RefundRevenuePayload): ApiResult<{ refund: Revenue; receivable: AccountReceivable | null }> =>
    api.post(`/v1/revenues/${id}/refund`, data),
  listAccountReceivables: (
    clinicId: EntityId,
    startDate: string,
    endDate: string,
    filters?: {
      status?: string;
      patientId?: EntityId;
      professionalId?: EntityId;
      sectorId?: string;
      paymentMethod?: string;
    },
  ): ApiResult<AccountReceivable[]> =>
    api.get("/v1/account-receivables", {
      params: {
        clinic_id: clinicId,
        start_date: startDate,
        end_date: endDate,
        status: filters?.status,
        patient_id: filters?.patientId,
        professional_id: filters?.professionalId,
        sector_id: filters?.sectorId,
        payment_method: filters?.paymentMethod,
      },
    }),
  listAccountReceivablesPaginated: (clinicId: EntityId, params: FinancialListParams): ApiResult<PaginatedResponse<AccountReceivable>> =>
    api.get("/v1/account-receivables", { params: { clinic_id: clinicId, ...params } }),
  createAccountReceivable: (data: CreateAccountReceivablePayload): ApiResult<AccountReceivable> =>
    api.post("/v1/account-receivables", data),
  updateAccountReceivable: (id: EntityId, data: UpdateAccountReceivablePayload): ApiResult<AccountReceivable> =>
    api.patch(`/v1/account-receivables/${id}`, data),
  receiveAccountReceivable: (id: EntityId, data: ReceiveAccountReceivablePayload): ApiResult<AccountReceivable> =>
    api.post(`/v1/account-receivables/${id}/receive`, data),
  cancelAccountReceivable: (id: EntityId): ApiResult<AccountReceivable> =>
    api.post(`/v1/account-receivables/${id}/cancel`),
  confirmAccountReceivableMaintenance: (id: EntityId, reason: string): ApiResult<AccountReceivable> =>
    api.post(`/v1/financial/reconciliation/account-receivables/${id}/confirm-maintenance`, { reason }),
  listExpenses: (clinicId: EntityId, startDate: string, endDate: string, sectorId?: string): ApiResult<Expense[]> =>
    api.get("/v1/expenses", {
      params: {
        clinic_id: clinicId,
        start_date: startDate,
        end_date: endDate,
        sector_id: sectorId,
      },
    }),
  listExpensesPaginated: (clinicId: EntityId, params: FinancialListParams): ApiResult<PaginatedResponse<Expense>> =>
    api.get("/v1/expenses", { params: { clinic_id: clinicId, ...params } }),
  createExpense: (data: CreateExpensePayload): ApiResult<Expense> => api.post("/v1/expenses", data),
  deleteExpense: (id: EntityId): ApiResult<void> => api.delete(`/v1/expenses/${id}`),
};

export const metricsApi = {
  dashboard: (clinicId: EntityId, startDate?: string, endDate?: string, sectorId?: string): ApiResult<DashboardMetrics> =>
    api.get("/v1/metrics/dashboard", {
      params: {
        clinic_id: clinicId,
        start_date: startDate,
        end_date: endDate,
        sector_id: sectorId,
      },
    }),
  dashboardDetails: (clinicId: EntityId, params: {
    detail_type: DashboardIndicatorDetailType; start_date: string; end_date: string; sector_id?: string;
    search?: string; sort_by?: "recent" | "name" | "status"; sort_order?: "asc" | "desc";
    page: number; page_size: 20 | 50 | 100;
  }): ApiResult<DashboardIndicatorDetailResponse> =>
    api.get("/v1/metrics/dashboard-details", { params: { clinic_id: clinicId, ...params } }),
  cacLtvRoi: (clinicId: EntityId, months: number, startDate?: string, endDate?: string, sectorId?: string): ApiResult<MarketingMetrics> =>
    api.get("/v1/metrics/cac-ltv-roi", { params: { clinic_id: clinicId, months, start_date: startDate, end_date: endDate, sector_id: sectorId } }),
  careExperience: (
    clinicId: EntityId,
    startDate?: string,
    endDate?: string,
    appointmentType?: string,
    sectorId?: string,
  ): ApiResult<CareExperienceMetrics> =>
    api.get("/v1/metrics/care-experience", {
      params: {
        clinic_id: clinicId,
        start_date: startDate,
        end_date: endDate,
        appointment_type: appointmentType,
        sector_id: sectorId,
      },
    }),
  marketingSectorDetails: (
    clinicId: EntityId,
    params: {
      sectorId: string;
      sector_id?: string;
      metric: MarketingSectorDetailMetric;
      start_date: string;
      end_date: string;
      search?: string;
      origin?: string;
      campaign_id?: string;
      page: number;
      page_size: 20 | 50 | 100;
    },
  ): ApiResult<PaginatedResponse<MarketingSectorDetailItem>> =>
    api.get("/v1/metrics/marketing-sector-details", {
      params: {
        clinic_id: clinicId,
        ...params,
        sector_id: params.sector_id ?? params.sectorId,
        sectorId: undefined,
      },
    }),
  careDetails: (clinicId: EntityId, params: AttendanceDetailListParams): ApiResult<AttendanceDetailResponse> =>
    api.get("/v1/attendance/details", { params: { clinic_id: clinicId, ...params } }),
  sectorDetails: (clinicId: EntityId, params: AttendanceDetailListParams): ApiResult<AttendanceDetailResponse> =>
    api.get("/v1/attendance/sector-details", { params: { clinic_id: clinicId, ...params } }),
};

export const expensesApi = {
  categories: (clinicId: EntityId): ApiResult<ExpenseCategoryRecord[]> =>
    api.get("/v1/expense-categories", { params: { clinic_id: clinicId } }),
  summary: (clinicId: EntityId, params: ExpenseListParams): ApiResult<ExpenseSummary> =>
    api.get("/v1/expense-payables/summary", { params: { clinic_id: clinicId, ...params } }),
  list: (clinicId: EntityId, params: ExpenseListParams): ApiResult<PaginatedResponse<ExpensePayable>> =>
    api.get("/v1/expense-payables", { params: { clinic_id: clinicId, ...params } }),
  create: (data: Record<string, unknown>): ApiResult<ExpensePayable> =>
    api.post("/v1/expense-payables", data),
  approve: (id: EntityId): ApiResult<ExpensePayable> =>
    api.post(`/v1/expense-payables/${id}/approve`),
  pay: (id: EntityId, data: { amount: number; payment_date?: string; payment_method?: string; idempotency_key?: string }): ApiResult<ExpensePayable> =>
    api.post(`/v1/expense-payables/${id}/pay`, data),
  reversePayment: (id: EntityId, data: { amount?: number; reason: string; reversed_at?: string; idempotency_key?: string }): ApiResult<ExpensePayable> =>
    api.post(`/v1/expense-payments/${id}/reverse`, data),
  cancel: (id: EntityId, reason: string): ApiResult<ExpensePayable> =>
    api.post(`/v1/expense-payables/${id}/cancel`, { reason }),
  createRecurrence: (data: Record<string, unknown>): ApiResult<Record<string, unknown>> =>
    api.post("/v1/expense-recurrences", data),
  generateRecurrence: (id: EntityId, untilDate: string): ApiResult<Record<string, unknown>> =>
    api.post(`/v1/expense-recurrences/${id}/generate`, { until_date: untilDate }),
  backfillLegacy: (clinicId: EntityId): ApiResult<Record<string, number>> =>
    api.post("/v1/expense-payables/backfill-legacy", undefined, { params: { clinic_id: clinicId } }),
  exportCsv: (clinicId: EntityId, params: ExpenseListParams): ApiResult<Blob> =>
    api.get("/v1/expense-payables/export", { params: { clinic_id: clinicId, ...params }, responseType: "blob" }),
};

export const expenseAllocationApi = {
  getSettings: (clinicId: EntityId): ApiResult<ExpenseAllocationSettings> =>
    api.get("/v1/expense-allocation/settings", { params: { clinic_id: clinicId } }),
  updateSettings: (clinicId: EntityId, managerialAllocationEnabled: boolean): ApiResult<ExpenseAllocationSettings> =>
    api.put("/v1/expense-allocation/settings", {
      clinic_id: clinicId,
      managerial_allocation_enabled: managerialAllocationEnabled,
    }),
  listCategories: (clinicId: EntityId): ApiResult<ExpenseCategoryRecord[]> =>
    api.get("/v1/expense-allocation/categories", { params: { clinic_id: clinicId } }),
  updateCategoryEligibility: (clinicId: EntityId, categoryId: EntityId, eligibility: ExpenseAllocationEligibility): ApiResult<ExpenseCategoryRecord> =>
    api.put(`/v1/expense-allocation/categories/${categoryId}`, {
      clinic_id: clinicId,
      managerial_allocation_eligibility: eligibility,
    }),
  simulate: (clinicId: EntityId, data: { start_date: string; end_date: string; category_id?: EntityId | null }): ApiResult<ExpenseAllocationSimulation> =>
    api.post("/v1/expense-allocation/simulations", { clinic_id: clinicId, ...data }),
  apply: (clinicId: EntityId, data: {
    start_date: string;
    end_date: string;
    category_id?: EntityId | null;
    simulation_fingerprint: string;
    idempotency_key: string;
    notes?: string | null;
  }): ApiResult<ExpenseAllocationApplication> =>
    api.post("/v1/expense-allocation/applications", { clinic_id: clinicId, ...data }),
  listApplications: (clinicId: EntityId, params?: {
    status?: ExpenseAllocationApplicationStatus;
    start_date?: string;
    end_date?: string;
    page?: number;
    page_size?: number;
  }): ApiResult<ExpenseAllocationApplicationList> =>
    api.get("/v1/expense-allocation/applications", { params: { clinic_id: clinicId, ...params } }),
  getApplication: (clinicId: EntityId, applicationId: EntityId): ApiResult<ExpenseAllocationApplication> =>
    api.get(`/v1/expense-allocation/applications/${applicationId}`, { params: { clinic_id: clinicId } }),
  reverseApplication: (clinicId: EntityId, applicationId: EntityId, reason: string): ApiResult<ExpenseAllocationApplication> =>
    api.post(`/v1/expense-allocation/applications/${applicationId}/reverse`, { clinic_id: clinicId, reason }),
  listRules: (clinicId: EntityId, params?: { status?: string; scope_type?: string; expense_category_id?: string }): ApiResult<ExpenseAllocationRule[]> =>
    api.get("/v1/expense-allocation/rules", { params: { clinic_id: clinicId, ...params } }),
  createRule: (data: Record<string, unknown>): ApiResult<ExpenseAllocationRule> =>
    api.post("/v1/expense-allocation/rules", data),
  getRule: (clinicId: EntityId, ruleId: EntityId): ApiResult<ExpenseAllocationRule> =>
    api.get(`/v1/expense-allocation/rules/${ruleId}`, { params: { clinic_id: clinicId } }),
  updateRule: (clinicId: EntityId, ruleId: EntityId, data: Record<string, unknown>): ApiResult<ExpenseAllocationRule> =>
    api.put(`/v1/expense-allocation/rules/${ruleId}`, { clinic_id: clinicId, ...data }),
  activateRule: (clinicId: EntityId, ruleId: EntityId): ApiResult<ExpenseAllocationRule> =>
    api.post(`/v1/expense-allocation/rules/${ruleId}/activate`, { clinic_id: clinicId }),
  createNewVersion: (clinicId: EntityId, ruleId: EntityId, data: Record<string, unknown>): ApiResult<ExpenseAllocationRule> =>
    api.post(`/v1/expense-allocation/rules/${ruleId}/new-version`, { clinic_id: clinicId, ...data }),
  inactivateRule: (clinicId: EntityId, ruleId: EntityId, reason: string): ApiResult<ExpenseAllocationRule> =>
    api.post(`/v1/expense-allocation/rules/${ruleId}/inactivate`, { clinic_id: clinicId, reason }),
  history: (clinicId: EntityId, ruleId: EntityId): ApiResult<ExpenseAllocationRule[]> =>
    api.get(`/v1/expense-allocation/rules/${ruleId}/history`, { params: { clinic_id: clinicId } }),
  exportApplicationsCsv: (clinicId: EntityId, params?: {
    status?: ExpenseAllocationApplicationStatus | "ALL";
    start_date?: string;
    end_date?: string;
    currency_code?: string;
    sector_id?: string;
    application_id?: string;
  }): ApiResult<Blob> =>
    api.get("/v1/expense-allocation/applications/export", {
      params: { clinic_id: clinicId, ...params },
      responseType: "blob",
    }),
  exportApplicationsReport: (clinicId: EntityId, format: ManagerialReportFormat, params?: {
    status?: ExpenseAllocationApplicationStatus | "ALL";
    start_date?: string;
    end_date?: string;
    currency_code?: string;
    sector_id?: string;
    application_id?: string;
  }): ApiResult<Blob> =>
    api.get("/v1/expense-allocation/applications/report", {
      params: { clinic_id: clinicId, format, ...params },
      responseType: "blob",
    }),
  exportSnapshotsCsv: (clinicId: EntityId, params?: {
    status?: ExpenseAllocationApplicationStatus | "ALL";
    start_date?: string;
    end_date?: string;
    currency_code?: string;
    sector_id?: string;
    application_id?: string;
  }): ApiResult<Blob> =>
    api.get("/v1/expense-allocation/snapshots/export", {
      params: { clinic_id: clinicId, ...params },
      responseType: "blob",
    }),
  exportSnapshotsReport: (clinicId: EntityId, format: ManagerialReportFormat, params?: {
    status?: ExpenseAllocationApplicationStatus | "ALL";
    start_date?: string;
    end_date?: string;
    currency_code?: string;
    sector_id?: string;
    application_id?: string;
  }): ApiResult<Blob> =>
    api.get("/v1/expense-allocation/snapshots/report", {
      params: { clinic_id: clinicId, format, ...params },
      responseType: "blob",
    }),
};

export const clinicalProductionsApi = {
  list: async (
    clinicId: EntityId,
    params: {
      start_date?: string;
      end_date?: string;
      professional_id?: string;
      sector_id?: string;
      catalog_item_id?: string;
      production_source?: string;
      status?: string;
      value_classification?: string;
      page?: number;
      page_size?: 20 | 50 | 100;
    },
  ): ApiResult<PaginatedResponse<ClinicalProduction>> => {
    const response = await api.get<ClinicalProductionListPayload<ClinicalProduction>>("/v1/clinical-productions", {
      params: { clinic_id: clinicId, ...params },
    });
    return { ...response, data: normalizeClinicalProductionList(response.data) };
  },
  summary: (
    clinicId: EntityId,
    params: {
      start_date?: string;
      end_date?: string;
      professional_id?: string;
      sector_id?: string;
      catalog_item_id?: string;
      production_source?: string;
      status?: string;
      value_classification?: string;
    },
  ): ApiResult<ClinicalProductionSummary> =>
    api.get("/v1/clinical-productions/summary", { params: { clinic_id: clinicId, ...params } }),
  details: (clinicId: EntityId, params: {
    detail_type: ClinicalProductionIndicatorDetailType; start_date: string; end_date: string;
    professional_id?: string; sector_id?: string; catalog_item_id?: string; production_source?: string; status?: string; value_classification?: string;
    search?: string; sort_by?: "recent" | "patient_name" | "value"; sort_order?: "asc" | "desc";
    page: number; page_size: 20 | 50 | 100;
  }): ApiResult<ClinicalProductionIndicatorDetailResponse> =>
    api.get("/v1/clinical-productions/details", { params: { clinic_id: clinicId, ...params } }),
  backfill: (clinicId: EntityId, limit?: number): ApiResult<{
    scanned: number;
    created: number;
    skippedExisting: number;
    skippedIneligible: number;
    unknownValue: number;
  }> => api.post("/v1/clinical-productions/backfill", { clinic_id: clinicId, limit }),
  cancelForAppointment: (appointmentId: EntityId, reason: string): ApiResult<ClinicalProduction | null> =>
    api.post(`/v1/appointments/${appointmentId}/clinical-production/cancel`, { reason }),
};

export const professionalPayoutsApi = {
  listRules: (clinicId: EntityId, professionalId?: EntityId): ApiResult<ProfessionalPayoutRule[]> =>
    api.get("/v1/professional-payout-rules", { params: { clinic_id: clinicId, professional_id: professionalId } }),
  createRule: (data: {
    clinic_id: EntityId;
    professional_id: EntityId;
    sector_id?: EntityId | null;
    catalog_item_id?: EntityId | null;
    rule_type: ProfessionalPayoutRuleType;
    fixed_amount?: number | null;
    percentage_rate?: number | null;
    currency_code?: string;
    effective_from: string;
    effective_to?: string | null;
    allow_courtesy_payout?: boolean;
  }): ApiResult<ProfessionalPayoutRule> => api.post("/v1/professional-payout-rules", data),
  deactivateRule: (ruleId: EntityId, effectiveTo?: string | null): ApiResult<ProfessionalPayoutRule> =>
    api.post(`/v1/professional-payout-rules/${ruleId}/deactivate`, { effective_to: effectiveTo }),
  list: async (
    clinicId: EntityId,
    params: {
      start_date?: string;
      end_date?: string;
      professional_id?: string;
      sector_id?: string;
      catalog_item_id?: string;
      rule_type?: string;
      status?: string;
      value_classification?: string;
      payment_state?: string;
      page?: number;
      page_size?: 20 | 50 | 100;
    },
  ): ApiResult<PaginatedResponse<ProfessionalPayout>> => {
    const response = await api.get<TotalPaginatedPayload<ProfessionalPayout>>("/v1/professional-payouts", {
      params: { clinic_id: clinicId, ...params },
    });
    return { ...response, data: normalizeTotalPaginatedPayload(response.data, "Professional payout") };
  },
  summary: (
    clinicId: EntityId,
    params: {
      start_date?: string;
      end_date?: string;
      professional_id?: string;
      sector_id?: string;
      catalog_item_id?: string;
      rule_type?: string;
      status?: string;
      value_classification?: string;
      payment_state?: string;
    },
  ): ApiResult<ProfessionalPayoutSummary> =>
    api.get("/v1/professional-payouts/summary", { params: { clinic_id: clinicId, ...params } }),
  backfill: (clinicId: EntityId, limit?: number): ApiResult<{
    scanned: number;
    created: number;
    skippedExisting: number;
    blocked: number;
    skippedIneligible: number;
  }> => api.post("/v1/professional-payouts/backfill", { clinic_id: clinicId, limit }),
  generateForProduction: (clinicId: EntityId, clinicalProductionId: EntityId): ApiResult<ProfessionalPayout> =>
    api.post(`/v1/clinical-productions/${clinicalProductionId}/professional-payout/generate`, { clinic_id: clinicId }),
  approve: (payoutId: EntityId): ApiResult<ProfessionalPayout> =>
    api.post(`/v1/professional-payouts/${payoutId}/approve`, {}),
  pay: (
    payoutId: EntityId,
    data: { amount: number; paid_at?: string; payment_method?: string; idempotency_key?: string; notes?: string | null },
  ): ApiResult<ProfessionalPayout> => api.post(`/v1/professional-payouts/${payoutId}/pay`, data),
  cancel: (payoutId: EntityId, reason: string): ApiResult<ProfessionalPayout | null> =>
    api.post(`/v1/professional-payouts/${payoutId}/cancel`, { reason }),
};

export const managementOverviewApi = {
  overview: (
    clinicId: EntityId,
    params: {
      start_date?: string;
      end_date?: string;
      sector_id?: string;
      professional_id?: string;
      seller_id?: string;
      catalog_item_id?: string;
      origin?: string;
      status?: string;
      currency_code?: string;
      classification_mode?: ManagementOverviewClassificationMode;
    },
  ): ApiResult<ManagementOverview> =>
    api.get("/v1/management-overview", { params: { clinic_id: clinicId, ...params } }),
  expenseAllocationImpact: (
    clinicId: EntityId,
    params: {
      start_date?: string;
      end_date?: string;
      sector_id?: string;
      currency_code?: string;
      classification_mode?: ManagementOverviewClassificationMode;
    },
  ): ApiResult<ManagementExpenseAllocationImpact> =>
    api.get("/v1/management-overview/expense-allocation-impact", { params: { clinic_id: clinicId, ...params } }),
  expenseAllocationImpactExportCsv: (
    clinicId: EntityId,
    params: {
      start_date?: string;
      end_date?: string;
      sector_id?: string;
      currency_code?: string;
      classification_mode?: ManagementOverviewClassificationMode;
    },
  ): ApiResult<Blob> =>
    api.get("/v1/management-overview/expense-allocation-impact/export", {
      params: { clinic_id: clinicId, ...params },
      responseType: "blob",
    }),
  expenseAllocationImpactReport: (
    clinicId: EntityId,
    format: ManagerialReportFormat,
    params: {
      start_date?: string;
      end_date?: string;
      sector_id?: string;
      currency_code?: string;
      classification_mode?: ManagementOverviewClassificationMode;
    },
  ): ApiResult<Blob> =>
    api.get("/v1/management-overview/expense-allocation-impact/report", {
      params: { clinic_id: clinicId, format, ...params },
      responseType: "blob",
    }),
  exportCsv: (
    clinicId: EntityId,
    params: {
      start_date?: string;
      end_date?: string;
      sector_id?: string;
      professional_id?: string;
      seller_id?: string;
      catalog_item_id?: string;
      origin?: string;
      status?: string;
      currency_code?: string;
      classification_mode?: ManagementOverviewClassificationMode;
    },
  ): ApiResult<Blob> =>
    api.get("/v1/management-overview/export", {
      params: { clinic_id: clinicId, ...params },
      responseType: "blob",
    }),
};

export const managerialAccrualApi = {
  summary: (
    clinicId: EntityId,
    params: {
      start_date?: string;
      end_date?: string;
      currency_code?: string;
      classification_mode?: ManagementOverviewClassificationMode;
      sector_id?: string;
      professional_id?: string;
      category_id?: string;
    },
  ): ApiResult<Record<string, unknown>> =>
    api.get("/v1/managerial-accrual/summary", { params: { clinic_id: clinicId, ...params } }),
  dataQuality: (
    clinicId: EntityId,
    params: {
      start_date?: string;
      end_date?: string;
      currency_code?: string;
      classification_mode?: ManagementOverviewClassificationMode;
    },
  ): ApiResult<Record<string, unknown>> =>
    api.get("/v1/managerial-accrual/data-quality", { params: { clinic_id: clinicId, ...params } }),
  expensesWithoutCompetence: (
    clinicId: EntityId,
    params: { currency_code?: string; page?: number; page_size?: 20 | 50 | 100 },
  ): ApiResult<PaginatedResponse<ExpensePayable>> =>
    api.get("/v1/managerial-accrual/expenses-without-competence", { params: { clinic_id: clinicId, ...params } }),
  assignExpenseCompetence: (
    payableId: EntityId,
    data: { competence_date: string | null; competence_classification?: ManagerialCompetenceClassification; reason: string },
  ): ApiResult<ExpensePayable> =>
    api.post(`/v1/managerial-accrual/expense-payables/${payableId}/competence`, data),
  backfillExpenseCompetence: (clinicId: EntityId, limit?: number): ApiResult<Record<string, number>> =>
    api.post("/v1/managerial-accrual/backfill-expense-competence", undefined, { params: { clinic_id: clinicId, limit } }),
};

export const managerialIncomeStatementApi = {
  statement: (
    clinicId: EntityId,
    params: {
      start_date?: string;
      end_date?: string;
      currency_code?: string;
      classification_mode?: ManagerialIncomeStatementClassificationMode;
      allocation_view?: ManagerialIncomeStatementAllocationView;
      sector_id?: string;
      professional_id?: string;
      category_id?: string;
    },
  ): ApiResult<ManagerialIncomeStatement> =>
    api.get("/v1/managerial-income-statement", { params: { clinic_id: clinicId, ...params } }),
  drilldown: (
    clinicId: EntityId,
    params: {
      line_code: ManagerialIncomeStatementLineCode;
      start_date?: string;
      end_date?: string;
      currency_code?: string;
      classification_mode?: ManagerialIncomeStatementClassificationMode;
      allocation_view?: ManagerialIncomeStatementAllocationView;
      sector_id?: string;
      professional_id?: string;
      category_id?: string;
      page?: number;
      page_size?: number;
    },
  ): ApiResult<PaginatedResponse<Record<string, unknown>>> =>
    api.get("/v1/managerial-income-statement/drilldown", { params: { clinic_id: clinicId, ...params } }),
  allocationDetails: (
    clinicId: EntityId,
    params: {
      start_date?: string;
      end_date?: string;
      currency_code?: string;
      classification_mode?: ManagerialIncomeStatementClassificationMode;
      allocation_view?: ManagerialIncomeStatementAllocationView;
      sector_id?: string;
      professional_id?: string;
      category_id?: string;
      page?: number;
      page_size?: number;
    },
  ): ApiResult<PaginatedResponse<Record<string, unknown>>> =>
    api.get("/v1/managerial-income-statement/allocation-details", { params: { clinic_id: clinicId, ...params } }),
  exportCsv: (
    clinicId: EntityId,
    params: {
      start_date?: string;
      end_date?: string;
      currency_code?: string;
      classification_mode?: ManagerialIncomeStatementClassificationMode;
      sector_id?: string;
      professional_id?: string;
      category_id?: string;
    },
  ): ApiResult<Blob> =>
    api.get("/v1/managerial-income-statement/export", {
      params: { clinic_id: clinicId, ...params },
      responseType: "blob",
    }),
  allocationExportCsv: (
    clinicId: EntityId,
    params: {
      start_date?: string;
      end_date?: string;
      currency_code?: string;
      classification_mode?: ManagerialIncomeStatementClassificationMode;
      allocation_view: Exclude<ManagerialIncomeStatementAllocationView, "ORIGINAL">;
      sector_id?: string;
      professional_id?: string;
      category_id?: string;
    },
  ): ApiResult<Blob> =>
    api.get("/v1/managerial-income-statement/allocation-export", {
      params: { clinic_id: clinicId, ...params },
      responseType: "blob",
    }),
  allocationReport: (
    clinicId: EntityId,
    format: ManagerialReportFormat,
    params: {
      start_date?: string;
      end_date?: string;
      currency_code?: string;
      classification_mode?: ManagerialIncomeStatementClassificationMode;
      allocation_view: Exclude<ManagerialIncomeStatementAllocationView, "ORIGINAL">;
      sector_id?: string;
      professional_id?: string;
      category_id?: string;
    },
  ): ApiResult<Blob> =>
    api.get("/v1/managerial-income-statement/allocation-report", {
      params: { clinic_id: clinicId, format, ...params },
      responseType: "blob",
    }),
};

export const treatmentFeedbackApi = {
  list: (clinicId: EntityId, patientId?: EntityId): ApiResult<TreatmentFeedback[]> =>
    api.get("/v1/treatment-feedbacks", {
      params: {
        clinic_id: clinicId,
        patient_id: patientId,
      },
    }),
  create: (data: CreateTreatmentFeedbackPayload): ApiResult<TreatmentFeedback> =>
    api.post("/v1/treatment-feedbacks", data),
  listRequests: (clinicId: EntityId): ApiResult<TreatmentFeedbackRequest[]> =>
    api.get("/v1/treatment-feedback-requests", {
      params: {
        clinic_id: clinicId,
      },
    }),
  issueDeliveryToken: (id: EntityId): ApiResult<{ token: string }> =>
    api.post(`/v1/treatment-feedback-requests/${id}/delivery-token`),
  renewDeliveryToken: (id: EntityId): ApiResult<{ token: string; expires_at: string; expiresAt?: string }> =>
    api.post(`/v1/treatment-feedback-requests/${id}/renew-delivery-token`),
};

export const surveyApi = {
  getByToken: (token: string): ApiResult<PublicNpsSurvey> => api.get(`/public/nps/${token}`),
  submitByToken: (token: string, data: PublicTreatmentFeedbackResponsePayload): ApiResult<TreatmentFeedback> =>
    api.post(`/public/nps/${token}/submit`, data),
};

export const alertsApi = {
  operational: (clinicId: EntityId): ApiResult<OperationalAlertsSummary> =>
    api.get("/v1/alerts/operational", { params: { clinic_id: clinicId } }),
};

export default api;
